/**
 * MODULE: platform/browser/frontend/utils/launchBrowserFromAssistant.ts
 * PURPOSE: Launch AI Browser from CRM assistant dock with page context and inferred mission.
 */

import type { BrowserMissionId } from '../data/browserMissions';
import { openBrowserWithLaunch } from './browserLaunch';

export function inferBrowserMissionFromPath(path: string, contextHint?: string): BrowserMissionId {
  const hint = (contextHint ?? '').toLowerCase();
  if (hint.includes('linkedin')) return 'linkedin';
  if (hint.includes('salesforce')) return 'salesforce';
  if (hint.includes('social') || hint.includes('twitter') || hint.includes(' x ')) return 'twitter';

  const p = path.toLowerCase();
  if (p.includes('linkedin')) return 'linkedin';
  if (p.includes('salesforce')) return 'salesforce';
  if (
    p.includes('/crm') ||
    p.startsWith('/accounts') ||
    p.includes('/companies') ||
    p.includes('/contacts') ||
    p.includes('/deals')
  ) {
    return 'brazn-crm';
  }
  return 'google';
}

export function buildAssistantBrowserTask(instruction: string, pageTitle?: string): string {
  const task = instruction.trim();
  if (task) return task;
  const title = (pageTitle ?? '').trim();
  if (title) {
    return `On ${title}, complete the highest-priority sales task using the attached Brazn context.`;
  }
  return 'Complete the highest-priority task on this page using the attached Brazn context.';
}

export function runBrowserLaunchFromAssistant(
  navigate: (path: string) => void,
  opts: {
    instruction?: string;
    prefillContext?: string;
    pagePath?: string;
    pageTitle?: string;
    suggestedPrompts?: string[];
    onBeforeNavigate?: () => void;
  },
): void {
  const pagePath = (opts.pagePath ?? '').trim();
  const prefill = (opts.prefillContext ?? '').trim();
  const instruction = buildAssistantBrowserTask(
    (opts.instruction ?? '').trim() || (opts.suggestedPrompts?.[0] ?? ''),
    opts.pageTitle,
  );
  const contextParts: string[] = [];
  if (prefill) contextParts.push(prefill);
  if (pagePath) contextParts.push(`Page path: ${pagePath}`);
  if ((opts.pageTitle ?? '').trim()) contextParts.push(`Page title: ${opts.pageTitle!.trim()}`);

  opts.onBeforeNavigate?.();
  openBrowserWithLaunch(
    {
      mission: inferBrowserMissionFromPath(pagePath, `${prefill} ${instruction}`),
      task: instruction,
      context: contextParts.join('\n\n'),
      autoRun: true,
    },
    navigate,
  );
}
